import { useEffect, useRef } from 'react';
import type { QuestionStep } from '../../../types/survey';
import type { SurveyAnswerValue } from '../../../types/surveyResponse';
import { SurveyMarkdown } from './SurveyMarkdown';

// Phase 6B D4 — per-type answer inputs (§4.5), mobile-first. Each renders a
// controlled input over the draft SurveyAnswerValue + reports changes up. The
// optional rating comment is handled separately by the runner.

const RESERVED_OTHER = '__other__';

export function defaultDraft(step: QuestionStep): SurveyAnswerValue {
  switch (step.type) {
    case 'yes_no':
      return { type: 'yes_no', value: undefined as unknown as boolean }; // unselected
    case 'multiple_choice_single':
      return { type: 'multiple_choice_single', optionId: '', otherText: null };
    case 'multiple_choice_multi':
      return { type: 'multiple_choice_multi', optionIds: [], otherText: null };
    case 'short_text':
      return { type: 'short_text', text: '' };
    case 'long_text':
      return { type: 'long_text', text: '' };
    case 'rating_scale':
      return { type: 'rating_scale', rating: step.config.type === 'rating_scale' ? step.config.min : 1 };
    case 'date':
      return { type: 'date', date: '' };
    case 'number':
      return { type: 'number', number: NaN };
    case 'name':
      return { type: 'name', name: '' };
  }
}

const cardBase =
  'flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors';

export function StepInput({
  step,
  value,
  onChange,
}: {
  step: QuestionStep;
  value: SurveyAnswerValue;
  onChange: (v: SurveyAnswerValue) => void;
}) {
  switch (value.type) {
    case 'yes_no':
      return <YesNo step={step} value={value} onChange={onChange} />;
    case 'multiple_choice_single':
      return <McSingle step={step} value={value} onChange={onChange} />;
    case 'multiple_choice_multi':
      return <McMulti step={step} value={value} onChange={onChange} />;
    case 'short_text':
    case 'long_text':
      return <TextInput step={step} value={value} onChange={onChange} />;
    case 'rating_scale':
      return <Rating step={step} value={value} onChange={onChange} />;
    case 'date':
      return <DateInput value={value} onChange={onChange} step={step} />;
    case 'number':
      return <NumberInput value={value} onChange={onChange} step={step} />;
    case 'name':
      return <NameInput value={value} onChange={onChange} />;
  }
}

function YesNo({ step, value, onChange }: { step: QuestionStep; value: Extract<SurveyAnswerValue, { type: 'yes_no' }>; onChange: (v: SurveyAnswerValue) => void }) {
  const cfg = step.config.type === 'yes_no' ? step.config : { trueLabel: undefined, falseLabel: undefined };
  const opt = (v: boolean, label: string) => (
    <button
      type="button"
      onClick={() => onChange({ type: 'yes_no', value: v })}
      className={cardBase + ' ' + (value.value === v ? 'border-kyck-500 bg-kyck-50 ring-1 ring-kyck-500' : 'border-slate-200 hover:border-slate-300')}
    >
      {label}
    </button>
  );
  return (
    <div className="grid gap-3">
      {opt(true, cfg.trueLabel || 'Yes')}
      {opt(false, cfg.falseLabel || 'No')}
    </div>
  );
}

function McSingle({ step, value, onChange }: { step: QuestionStep; value: Extract<SurveyAnswerValue, { type: 'multiple_choice_single' }>; onChange: (v: SurveyAnswerValue) => void }) {
  const cfg = step.config.type === 'multiple_choice_single' ? step.config : { options: [], allowOther: false };
  return (
    <div className="grid gap-2">
      {cfg.options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange({ type: 'multiple_choice_single', optionId: o.id, otherText: null })}
          className={cardBase + ' ' + (value.optionId === o.id ? 'border-kyck-500 bg-kyck-50 ring-1 ring-kyck-500' : 'border-slate-200 hover:border-slate-300')}
        >
          {o.label}
        </button>
      ))}
      {cfg.allowOther && (
        <div className={cardBase + ' ' + (value.optionId === RESERVED_OTHER ? 'border-kyck-500 bg-kyck-50 ring-1 ring-kyck-500' : 'border-slate-200')}>
          <span onClick={() => onChange({ type: 'multiple_choice_single', optionId: RESERVED_OTHER, otherText: value.otherText ?? '' })}>Other:</span>
          <input
            type="text"
            value={value.otherText ?? ''}
            onFocus={() => onChange({ type: 'multiple_choice_single', optionId: RESERVED_OTHER, otherText: value.otherText ?? '' })}
            onChange={(e) => onChange({ type: 'multiple_choice_single', optionId: RESERVED_OTHER, otherText: e.target.value })}
            className="flex-1 rounded border border-slate-300 px-2 py-1 text-sm"
            placeholder="Tell us"
          />
        </div>
      )}
    </div>
  );
}

function McMulti({ step, value, onChange }: { step: QuestionStep; value: Extract<SurveyAnswerValue, { type: 'multiple_choice_multi' }>; onChange: (v: SurveyAnswerValue) => void }) {
  const cfg = step.config.type === 'multiple_choice_multi' ? step.config : { options: [], minSelections: undefined, maxSelections: undefined };
  const toggle = (id: string) => {
    const has = value.optionIds.includes(id);
    onChange({ ...value, optionIds: has ? value.optionIds.filter((x) => x !== id) : [...value.optionIds, id] });
  };
  return (
    <div className="grid gap-2">
      {cfg.options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => toggle(o.id)}
          className={cardBase + ' ' + (value.optionIds.includes(o.id) ? 'border-kyck-500 bg-kyck-50 ring-1 ring-kyck-500' : 'border-slate-200 hover:border-slate-300')}
        >
          <span className={'flex h-4 w-4 items-center justify-center rounded border ' + (value.optionIds.includes(o.id) ? 'border-kyck-600 bg-kyck-600 text-white' : 'border-slate-300')}>
            {value.optionIds.includes(o.id) ? '✓' : ''}
          </span>
          {o.label}
        </button>
      ))}
      {(cfg.minSelections || cfg.maxSelections) && (
        <p className="text-xs text-slate-400">
          {cfg.minSelections ? `Select at least ${cfg.minSelections}. ` : ''}
          {cfg.maxSelections ? `Up to ${cfg.maxSelections}.` : ''}
        </p>
      )}
    </div>
  );
}

function TextInput({ step, value, onChange }: { step: QuestionStep; value: Extract<SurveyAnswerValue, { type: 'short_text' | 'long_text' }>; onChange: (v: SurveyAnswerValue) => void }) {
  const max = (step.config.type === 'short_text' || step.config.type === 'long_text' ? step.config.maxLength : undefined) ?? undefined;
  const placeholder = (step.config.type === 'short_text' || step.config.type === 'long_text' ? step.config.placeholder : undefined) ?? '';
  const common = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-base focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500';
  return (
    <div>
      {value.type === 'long_text' ? (
        <textarea rows={5} value={value.text} maxLength={max} placeholder={placeholder} onChange={(e) => onChange({ type: 'long_text', text: e.target.value })} className={common} />
      ) : (
        <input type="text" value={value.text} maxLength={max} placeholder={placeholder} onChange={(e) => onChange({ type: 'short_text', text: e.target.value })} className={common} />
      )}
      {max && <p className="mt-1 text-right text-xs text-slate-400">{value.text.length}/{max}</p>}
    </div>
  );
}

function Rating({ step, value, onChange }: { step: QuestionStep; value: Extract<SurveyAnswerValue, { type: 'rating_scale' }>; onChange: (v: SurveyAnswerValue) => void }) {
  const cfg = step.config.type === 'rating_scale' ? step.config : { min: 1, max: 10, minLabel: undefined, maxLabel: undefined };
  return (
    <div>
      <div className="mb-2 text-center text-3xl font-semibold text-kyck-700">{value.rating}</div>
      <input
        type="range"
        min={cfg.min}
        max={cfg.max}
        value={value.rating}
        onChange={(e) => onChange({ type: 'rating_scale', rating: Number(e.target.value) })}
        className="w-full accent-kyck-600"
      />
      <div className="mt-1 flex justify-between text-xs text-slate-400">
        <span>{cfg.minLabel || cfg.min}</span>
        <span>{cfg.maxLabel || cfg.max}</span>
      </div>
    </div>
  );
}

function DateInput({ value, onChange, step }: { value: Extract<SurveyAnswerValue, { type: 'date' }>; onChange: (v: SurveyAnswerValue) => void; step: QuestionStep }) {
  const cfg = step.config.type === 'date' ? step.config : { minDate: undefined, maxDate: undefined };
  return (
    <input
      type="date"
      value={value.date}
      min={cfg.minDate}
      max={cfg.maxDate}
      onChange={(e) => onChange({ type: 'date', date: e.target.value })}
      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-base focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
    />
  );
}

function NumberInput({ value, onChange, step }: { value: Extract<SurveyAnswerValue, { type: 'number' }>; onChange: (v: SurveyAnswerValue) => void; step: QuestionStep }) {
  const cfg = step.config.type === 'number' ? step.config : { min: undefined, max: undefined, unit: undefined };
  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        value={Number.isNaN(value.number) ? '' : value.number}
        min={cfg.min}
        max={cfg.max}
        onChange={(e) => onChange({ type: 'number', number: e.target.value === '' ? NaN : Number(e.target.value) })}
        className="w-40 rounded-lg border border-slate-300 px-3 py-2 text-base focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
      />
      {cfg.unit && <span className="text-sm text-slate-500">{cfg.unit}</span>}
    </div>
  );
}

function NameInput({ value, onChange }: { value: Extract<SurveyAnswerValue, { type: 'name' }>; onChange: (v: SurveyAnswerValue) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  return (
    <input
      ref={ref}
      type="text"
      value={value.name}
      onChange={(e) => onChange({ type: 'name', name: e.target.value })}
      placeholder="Your name"
      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-base focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
    />
  );
}

export function ContentPageView({ step }: { step: Extract<import('../../../types/survey').SurveyStep, { kind: 'content' }> }) {
  return (
    <div>
      {step.title && <h2 className="mb-3 text-lg font-semibold text-slate-900">{step.title}</h2>}
      {step.imageUrl && <img src={step.imageUrl} alt="" className="mb-4 w-full rounded-lg" />}
      <SurveyMarkdown markdown={step.bodyMarkdown} />
    </div>
  );
}
